import React, { useState, useCallback, Fragment, useEffect } from 'react';
import { Row, Checkbox, Radio } from 'antd';
import { ReactSortable } from 'react-sortablejs';
import PropTypes from 'prop-types';
import _ from 'lodash';

import Input from 'components/Input';
import DragIcon from 'components/Icons/DragIcon';

import './FormView.scss';
import ControllerWrapper from './ControllerWrapper';
import { PopoverComponent } from 'pages/Proposal/components/Publish/modals/PublishModals/PublishedProposalModal/RecipientModals';
import { EditControlForm } from './ControlEditOptions';
const FormView = ({
  form,
  columns,
  saveForm,
  handleFocus,
  setIsDraggable,
  configText,
  isFromModal = false,
  modifiedFormTitle,
  setModifiedFormTitle,
  formTitle,
  isRtl,
}) => {
  const [formFields, setFormFields] = useState(form ? form : []);
  const [updateFields, setUpdateFields] = useState(false);
  const [editFieldIndex, setEditFieldIndex] = useState(false);

  // eslint-disable-next-line
  const debouncedSave = useCallback(
    (formFieldsCopy, columnsCopy) => {
      // _.debounce((formFieldsCopy, columnsCopy) => {
      saveForm({ formCopy: formFieldsCopy, editable: false, columns: columnsCopy });
      // }, 1000),
    },
    [saveForm]
  );

  useEffect(() => {
    setFormFields(form);
  }, [form]);

  const handleChange = (event, index) => {
    const formFieldsCopy = [...formFields];
    const columnsCopy = columns;
    switch (event.target.type) {
      case 'text':
        formFieldsCopy[index].value = event.target.value;
        break;
      case 'checkbox':
        formFieldsCopy[index]['required'] = event.target.checked;
        break;
      default:
      // do nothing
    }
    setFormFields(formFieldsCopy);
    debouncedSave(formFieldsCopy, columnsCopy);
  };

  const sortFields = (newState) => {
    if (updateFields) {
      setFormFields(newState);
      const columnsCopy = columns;
      debouncedSave(newState, columnsCopy);
      setUpdateFields(false);
    }
  };

  const updateFieldValue = (index, value) => {
    const formFieldsCopy = [...formFields];
    // Preserve existing properties and merge with new values
    formFieldsCopy[index] = {
      ...formFieldsCopy[index],
      ...value, // Use question as key if available
    };
    setFormFields(formFieldsCopy);
    debouncedSave(formFieldsCopy, columns);
  };

  const removeElement = (index) => {
    const formFieldsCopy = [...formFields];
    formFieldsCopy[index] = formFieldsCopy.filter((item, i) => i !== index);
    setFormFields(formFieldsCopy);
    debouncedSave(formFieldsCopy, columns);
  };

  const onRequiredChange = (index, value) => {
    const formFieldsCopy = [...formFields];
    formFieldsCopy[index]['required'] = value;
    setFormFields(formFieldsCopy);
    debouncedSave(formFieldsCopy, columns);
  };

  return (
    <div className="modal-form-view-container">
      {!isFromModal && (
        <Row className="modal-form-view-header">
          <PopoverComponent
            direction="rightTop"
            icon={
              <ControllerWrapper
                showEdit={true}
                showDelete={false}
                showRequired={false}
                onEdit={() => setEditFieldIndex('head')}>
                <h3>{modifiedFormTitle || formTitle}</h3>
              </ControllerWrapper>
            }
            visible={editFieldIndex === 'head'}
            onVisibleChange={(visible) => !visible && setEditFieldIndex(false)}
            content={
              editFieldIndex === 'head' ? (
                <EditControlForm
                  item={{ formTitle: modifiedFormTitle || formTitle, type: 'head' }}
                  configText={configText}
                  onSave={(value) => setModifiedFormTitle(value)}
                  onCancel={() => setEditFieldIndex(false)}
                  isRtl={isRtl}
                />
              ) : (
                <></>
              )
            }></PopoverComponent>
        </Row>
      )}
      <Row className="modal-form-view-fields-row">
        <ReactSortable
          list={formFields}
          onUpdate={() => {
            setUpdateFields(true);
          }}
          setList={(newState) => {
            sortFields(newState);
          }}
          animation={400}
          handle=".handle"
          className={`modal-form-view ${columns === 2 ? 'form-list-two-columns' : 'column'}`}>
          {_.map(formFields, (item, listIndex) =>
            item.selected || item.saved ? (
              <div
                key={isFromModal ? `from_modal_${listIndex}` : listIndex}
                className={item.selected ? 'modal-form-list-item selected' : 'form-list-item'}>
                <Row
                  className="modal-form-field-row"
                  align={['checkbox', 'radio'].includes(item.type) ? 'start' : 'middle'}>
                  <div
                    style={{
                      flexWrap: 'nowrap',
                      marginTop: ['checkbox', 'radio'].includes(item.type) ? '7px' : '0px',
                    }}
                    className="handle-draggable handle"
                    onMouseDown={() => setIsDraggable(false)}
                    onMouseUp={() => setIsDraggable(true)}>
                    <DragIcon />
                  </div>
                  <div className="modal-form-field-wrapper">
                    <PopoverComponent
                      direction="rightTop"
                      icon={
                        <ControllerWrapper
                          style={
                            ['checkbox', 'radio'].includes(item.type)
                              ? { width: 'fit-content', minWidth: '300px' }
                              : {}
                          }
                          onEdit={() => setEditFieldIndex(listIndex)}
                          onDelete={() => removeElement(listIndex)}
                          onRequiredChange={(value) => onRequiredChange(listIndex, value)}
                          isRequired={item.required}>
                          {['checkbox', 'radio'].includes(item.type) ? (
                            <RadioCheckboxControllerWrapper item={item} />
                          ) : (
                            <>
                              <div className="modal-form-label">
                                <label>{configText(item.key, true)}</label>{' '}
                                {item.required ? <span className="required">*</span> : null}
                              </div>
                              <Input
                                type="text"
                                placeholder={
                                  item?.placeholder
                                    ? item.placeholder
                                    : `${configText('Enter', true)} ${configText(item.key, true)}`
                                }
                                onChange={(e) => handleChange(e, listIndex)}
                                onClick={handleFocus}
                                onFocus={handleFocus}
                                id={item.key}
                                autoFocus
                                defaultValue={item.value}
                                disabled={
                                  window.location.href.indexOf('/cd/') === -1 &&
                                  window.location.href.indexOf('/proposal-preview') === -1
                                    ? true
                                    : false
                                }
                              />
                            </>
                          )}
                        </ControllerWrapper>
                      }
                      visible={editFieldIndex === listIndex}
                      onVisibleChange={(visible) => !visible && setEditFieldIndex(false)}
                      content={
                        editFieldIndex === listIndex ? (
                          <EditControlForm
                            item={item}
                            configText={configText}
                            onSave={(value) => updateFieldValue(listIndex, value)}
                            onCancel={() => setEditFieldIndex(false)}
                            isRtl={isRtl}
                          />
                        ) : (
                          <></>
                        )
                      }
                    />
                  </div>
                </Row>
              </div>
            ) : (
              <Fragment key={listIndex}></Fragment>
            )
          )}
        </ReactSortable>
      </Row>
    </div>
  );
};

export const RadioCheckboxControllerWrapper = ({
  item,
  controlStyle = {},
  wrapperStyle = {},
  requiredStyle = {},
}) => {
  return (
    <Row
      className="radio-checkbox-controller-wrapper"
      style={{ maxWidth: 'fit-content', ...wrapperStyle }}>
      <Row>
        <span className="controller-label">{item.key}</span>&nbsp;
        {item.required ? (
          <span className="required" style={requiredStyle}>
            *
          </span>
        ) : null}
      </Row>
      <Row className="options-row-wrapper">
        {item.options?.map((option, index) => {
          return (
            <Row key={index} className="option-row">
              {item.type === 'radio' ? (
                <Radio checked={option.checked} style={controlStyle} />
              ) : (
                <Checkbox checked={option.checked} style={controlStyle} />
              )}
              <span className="option-label">{option.label}</span>
            </Row>
          );
        })}
      </Row>
    </Row>
  );
};

FormView.propTypes = {
  form: PropTypes.instanceOf(Array).isRequired,
  saveForm: PropTypes.func.isRequired,
  handleFocus: PropTypes.func.isRequired,
  configText: PropTypes.func.isRequired,
  isRtl: PropTypes.bool.isRequired,
};

export default FormView;
